import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { 
  Edit, History, Visibility, AccessTime, 
  LocationOn, VerifiedUser, ErrorOutline 
} from "@mui/icons-material";

// Local Imports
import { QK } from "../../../../../base/qk";
import { getVisitTimeline } from "../../../../../api/services/visitplan";
import useClientDetails from "../../../../../hooks/useClientDetails";
import { formatDateField, formatTimeToAmPm } from "../../../../../base/FormatTime";

// UI Components
import { 
  Avatar, Box, Typography, Chip, Paper, 
  Stack, Tooltip, Divider 
} from "@mui/material";

import TaskDetailsModal from "../../../../PopupModal/TaskDetailsModal";
import TableComponent from "../../../../TableComponent/TableComponent";
import HouseKeepingConfirmModal from "../../../../HouseKeeping/modals/HouseKeepingConfirmModal";

const CarePlanHistory = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  const { id: clientId } = useParams();
  const { fullName } = useClientDetails(clientId);

  const jobsPerPage = 10;

  // Data Fetching
  const { data: actionJobs, isLoading } = useQuery(
    [QK.scheduleTimeline, currentPage, clientId],
    () => getVisitTimeline(currentPage, clientId, "", "", "", "", "", 4),
    { keepPreviousData: true, enabled: !!clientId }
  );

  const totalRecords = actionJobs?.count || 0;
  const totalPages = Math.ceil(totalRecords / jobsPerPage);

  const handlePageChange = (event, page) => setCurrentPage(page);

  // Status Badge Logic
  const getStatusChip = (status) => {
    const statusMap = {
      completed: { color: "#2e7d32", bg: "#e8f5e9", label: "Completed" },
      pending: { color: "#ed6c02", bg: "#fff3e0", label: "Pending" },
      cancelled: { color: "#d32f2f", bg: "#ffebee", label: "Cancelled" },
    };
    const config = statusMap[status?.toLowerCase()] || { color: "#757575", bg: "#f5f5f5", label: status };
    
    return (
      <Chip 
        label={config.label} 
        size="small" 
        sx={{ 
          bgcolor: config.bg, 
          color: config.color, 
          fontWeight: 700, 
          borderRadius: '6px',
          fontSize: '11px'
        }} 
      />
    );
  };

  // Columns Definition
  const columns = useMemo(() => [
    {
      field: "build_for",
      headerName: "Schedule & Criticality",
      render: (value, row) => (
        <Stack spacing={0.5}>
          <Typography variant="body2" fontWeight={700} color="text.primary">
            {formatDateField(value)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <AccessTime sx={{ fontSize: 14 }} />
              {formatTimeToAmPm(row.scheduled_start)} - {formatTimeToAmPm(row.scheduled_end)}
            </Typography>
            {row.time_critical && (
              <Tooltip title="Strictly Time Critical">
                <ErrorOutline sx={{ fontSize: 16, color: 'error.main' }} />
              </Tooltip>
            )}
          </Stack>
        </Stack>
      ),
    },
    {
      field: "care_plan",
      headerName: "Care Plan / Action",
      render: (value, row) => (
        <Stack spacing={0.3}>
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {row.visit_name || "General Visit"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "employee_details",
      headerName: "Execution & Duration",
      render: (details) => {
        const firstEmp = details?.[0];
        return (
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={500}>
              {firstEmp ? `${firstEmp.first_name} ${firstEmp.last_name}` : "Unassigned"}
            </Typography>
            {firstEmp?.actual_start && (
              <Stack direction="row" spacing={1}>
                <Chip 
                  label={`In: ${firstEmp.actual_start}`} 
                  size="small" 
                  variant="outlined" 
                  sx={{ height: 18, fontSize: '10px', borderColor: 'success.light', color: 'success.main' }} 
                />
                <Typography variant="caption" color="text.secondary">
                  ⌛ {firstEmp.job_duration}
                </Typography>
              </Stack>
            )}
          </Stack>
        );
      }
    },
    {
      field: "status",
      headerName: "Event Status",
      render: (value, row) => (
        <Stack spacing={0.5} alignItems="flex-start">
          {getStatusChip(value)}
          {row.area && (
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled' }}>
              <LocationOn sx={{ fontSize: 12, mr: 0.2 }} /> {row.area}
            </Typography>
          )}
        </Stack>
      ),
    },
  ], []);

  const actions = [
    {
      icon: <Visibility fontSize="small" />,
      tooltip: "Quick View",
      sx: { color: "#6366f1", bgcolor: "#f5f3ff", "&:hover": { bgcolor: "#ddd6fe" } },
      handler: (job) => {
        setSelectedTask(job.id);
        setShowTaskModal(true);
      },
    },
    {
      icon: <Edit fontSize="small" />,
      tooltip: "Modify Record",
      sx: { color: "#f43f5e", bgcolor: "#fff1f2", "&:hover": { bgcolor: "#ffe4e6" } },
      handler: (job) => {
        setSelectedTask(job.id);
        setShowEditModal(true);
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Modern Aesthetic Header */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 4,
          background: "linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)",
          border: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar 
            sx={{ 
              bgcolor: "primary.main", 
              width: 52, 
              height: 52, 
              boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.5)" 
            }}
          >
            <History fontSize="medium" />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#1e293b', letterSpacing: '-0.5px' }}>
              Care Plan Timeline
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <VerifiedUser sx={{ fontSize: 14, color: 'success.main' }} /> {fullName}
            </Typography>
          </Box>
        </Stack>
        
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
           <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
             Total Records: {totalRecords}
           </Typography>
        </Box>
      </Paper>

      {/* Optimized Table Component */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 4, 
          border: "1px solid #e2e8f0", 
          overflow: "hidden",
          bgcolor: 'white' 
        }}
      >
        <TableComponent
          columns={columns}
          data={actionJobs?.results || []}
          actions={actions}
          isLoading={isLoading}
          pagination={{ totalPages, currentPage }}
          onPageChange={handlePageChange}
          size="medium"
        />
      </Paper>

      {/* Modals */}
      <TaskDetailsModal
        show={showTaskModal}
        handleClose={() => setShowTaskModal(false)}
        id={selectedTask}
      />
      <HouseKeepingConfirmModal
        jobId={selectedTask}
        open={showEditModal}
        handleClose={() => setShowEditModal(false)}
      />
    </Box>
  );
};

export default CarePlanHistory;